import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { switchMap, map, catchError, tap, delay, withLatestFrom, mergeMap } from 'rxjs/operators';
import { EMPTY, iif, Observable, of } from 'rxjs';
import {
    CreateProjectRequestPost,
    ParamsControllerService,
    PlanningsControllerService,
    ProjectResponseGetById,
    ProjectsControllerService,
    ReportsControllerService,
    UpdateProjectRequestPartial,
    VariantsControllerService
} from '@nx-customer-apps/api-planning-projects';
import { ApiErrors, ExternalTool, RoutePaths, RouterLinks } from '@nx-customer-apps/shared/enums';
import { ErrorResponse } from '@nx-customer-apps/shared/interfaces';
import { getUpdatedProperties } from '@nx-customer-apps/shared/utils';
import { LodashService, SnackbarService, WindowService } from '@nx-customer-apps/shared/services';
import { ErrorService, ExternalToolUrlService } from '../../services';
import { GlobalSpinnerService } from '../../core';
import { SettingsStore } from '../settings/settings.store';
import { AppSpecificDataActions } from '../app-specific-data/app-specific-data.actions';
import { ProjectAccessStore } from '../project-access/project-access.store';
import { ProjectActions } from './project.actions';
import { ProjectStore } from './project.store';
import { GlobalStore } from '../global/global.store';
import { HeatPumpIntermediaryMapper } from '../../heat-pump-intermediary/services';
import { ApplicationId } from '@nx-customer-apps/components';

@Injectable()
export class ProjectEffects {
    private actions$ = inject(Actions);
    private router = inject(Router);
    private globalSpinnerService = inject(GlobalSpinnerService);
    private snackbarService = inject(SnackbarService);
    private errorService = inject(ErrorService);
    private translate = inject(TranslateService);
    private settingsStore = inject(SettingsStore);
    private projectsControllerService = inject(ProjectsControllerService);
    private planningsControllerService = inject(PlanningsControllerService);
    private variantsControllerService = inject(VariantsControllerService);
    private reportsControllerService = inject(ReportsControllerService);
    private paramsControllerService = inject(ParamsControllerService);
    private heatPumpIntermediaryMapper = inject(HeatPumpIntermediaryMapper);
    private projectStore = inject(ProjectStore);
    private globalStore = inject(GlobalStore);
    private projectAccessStore = inject(ProjectAccessStore);
    private externalToolUrlService = inject(ExternalToolUrlService);
    private windowService = inject(WindowService);

    public getProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.getProject),
            tap(() => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.LOADING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.LOADING_PROJECT'
                });
            }),
            withLatestFrom(this.projectStore.project$, (action, project) => ({ action, project })),
            switchMap(({ action, project }) =>
                iif(
                    () => !!project,
                    of(project).pipe(tap(() => this.globalSpinnerService.reset())),
                    this.projectsControllerService.projectsControllerGetProject({
                        id: action.id,
                        detailsForApplicationId: ApplicationId.ViGuidePlanning as any
                    })
                ).pipe(
                    switchMap(response => [
                        AppSpecificDataActions.setAppSpecificData({
                            viguidePlanningSpecificData: (response as any).viguidePlanningSpecificData
                        }),
                        ProjectActions.getProjectSuccess({ project: response! })
                    ]),
                    catchError(error => {
                        this.projectAccessStore.unlockProject(action.id);
                        this.navigateTo404Page(error.error);
                        return this.handleApiErrors(error.error as ErrorResponse);
                    })
                )
            )
        )
    );

    public reloadProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.reloadProject),
            tap(() => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.LOADING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.LOADING_PROJECT'
                });
            }),
            switchMap(({ id, successActionOptions, successAction }) => {
                return this.projectsControllerService
                    .projectsControllerGetProject({
                        id,
                        detailsForApplicationId: ApplicationId.ViGuidePlanning as any
                    })
                    .pipe(
                        switchMap(response => [
                            AppSpecificDataActions.setAppSpecificData({
                                viguidePlanningSpecificData: (response as any).viguidePlanningSpecificData
                            }),
                            successAction({ ...successActionOptions, project: response })
                        ]),
                        catchError(error => this.handleApiErrors(error.error as ErrorResponse))
                    );
            })
        )
    );

    public createProjectPlanning$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.createProjectPlanning),
            tap(() => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.UPDATING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.UPDATING_PROJECT'
                });
            }),
            switchMap(({ projectId, body }) => {
                return this.planningsControllerService
                    .planningsControllerCreatePlanning({ body })
                    .pipe(map(() => ({ projectId, variantId: body.variantId })));
            }),
            delay(0),
            switchMap(({ projectId, variantId }) => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.LOADING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.LOADING_PROJECT'
                });
                return this.projectsControllerService
                    .projectsControllerGetProject({ id: projectId })
                    .pipe(map(response => ({ project: response, projectId, variantId })));
            }),
            switchMap(({ project, projectId, variantId }) => [
                ProjectActions.getProjectSuccess({
                    project,
                    redirectUrl: RoutePaths.BoilerPlanner.replace(':id', projectId).replace(':variantId', variantId)
                })
            ]),
            catchError(error => this.handleApiErrors(error.error as ErrorResponse))
        )
    );

    public updateProjectPlanning$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.updateProjectPlanning),
            tap(() => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.UPDATING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.UPDATING_PROJECT'
                });
            }),
            switchMap(({ projectId, planningId, body }) => {
                return this.planningsControllerService
                    .planningsControllerPatchPlanning({ id: planningId, body })
                    .pipe(map(() => ({ projectId })));
            }),
            delay(0),
            switchMap(({ projectId }) => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.LOADING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.LOADING_PROJECT'
                });
                return this.projectsControllerService
                    .projectsControllerGetProject({ id: projectId })
                    .pipe(map(response => ({ project: response })));
            }),
            switchMap(({ project }) => [ProjectActions.getProjectSuccess({ project })]),
            catchError(error => this.handleApiErrors(error.error as ErrorResponse))
        )
    );

    public updateProjectVariant$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.updateProjectVariant),
            tap(() => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.UPDATING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.UPDATING_PROJECT'
                });
            }),
            switchMap(({ projectId, variantId, body }) => {
                return this.variantsControllerService
                    .variantsControllerPatchVariant({ id: variantId, body })
                    .pipe(map(() => ({ projectId })));
            }),
            delay(0),
            switchMap(({ projectId }) => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.LOADING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.LOADING_PROJECT'
                });
                return this.projectsControllerService
                    .projectsControllerGetProject({ id: projectId })
                    .pipe(map(response => ({ project: response })));
            }),
            switchMap(({ project }) => [ProjectActions.getProjectSuccess({ project })]),
            catchError(error => this.handleApiErrors(error.error as ErrorResponse))
        )
    );

    public getProjectSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ProjectActions.getProjectSuccess),
                tap(action => {
                    if (action.redirectUrl) {
                        this.router.navigate([action.redirectUrl]);
                    }
                })
            ),
        { dispatch: false }
    );

    public updateProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.updateProject),
            tap(() => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.UPDATING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.UPDATING_PROJECT'
                });
            }),
            withLatestFrom(this.settingsStore.languageCode$, this.projectStore.project$, (action, languageCode, project) => ({
                action,
                languageCode,
                project,
                request: { ...action.request, languageCode }
            })),
            map(({ action, project, request }) => ({
                ...action,
                body: getUpdatedProperties(project!, request as UpdateProjectRequestPartial, ['viguidePlanningSpecificData', 'household'])
            })),
            switchMap(({ id, body, successAction, successActionOptions }) =>
                this.projectsControllerService.projectsControllerPatchProject({ id, body }).pipe(
                    delay(0),
                    map(() =>
                        ProjectActions.reloadProject({
                            id,
                            successAction,
                            successActionOptions
                        })
                    ),
                    catchError(error => this.handleApiErrors(error.error as ErrorResponse))
                )
            )
        )
    );

    public setAppSpecificData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.setAppSpecificData),
            tap(() => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.UPDATING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.UPDATING_PROJECT'
                });
            }),
            withLatestFrom(this.projectStore.project$, (action, project) => ({ action, project })),
            map(payload => ({
                ...payload,
                request: {
                    viguidePlanningSpecificData: {
                        ...payload.project?.viguidePlanningSpecificData,
                        data: {
                            ...payload.project?.viguidePlanningSpecificData?.data,
                            ...payload.action.data
                        }
                    }
                }
            })),
            map(({ project, request }) => ({
                id: project?.id!,
                body: getUpdatedProperties(project!, request as UpdateProjectRequestPartial, ['viguidePlanningSpecificData'])
            })),
            switchMap(({ id, body }) =>
                this.projectsControllerService.projectsControllerPatchProject({ id, body }).pipe(
                    delay(0),
                    map(() =>
                        AppSpecificDataActions.setAppSpecificData({
                            viguidePlanningSpecificData: body.viguidePlanningSpecificData!
                        })
                    ),
                    catchError(error => this.handleApiErrors(error.error as ErrorResponse))
                )
            )
        )
    );

    public migrationVerificationSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ProjectActions.migrationVerificationSuccess),
                tap(({ callback, project }) => {
                    if (callback) {
                        callback();
                        return;
                    }
                    this.router.navigate([`${RouterLinks.Project}${project.id}`]);
                })
            ),
        { dispatch: false }
    );

    public getHeatPumpToolsSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ProjectActions.getHeatPumpToolsSuccess),
                delay(0),
                tap(({ project, variantId }) => {
                    this.globalSpinnerService.next({
                        isShown: true,
                        loadingText: 'LOADER.TEXT.LOADING_PROJECT',
                        loadingSubtext: 'LOADER.SUBTEXT.LOADING_PROJECT'
                    });
                    this.externalToolUrlService.redirectToExternalToolByProjectId(ExternalTool.HeatPumpPlanner, {
                        projectId: project.id,
                        variantId,
                        navigateTo: 'system',
                        countryCode: project.countryCode
                    });
                })
            ),
        { dispatch: false }
    );

    public getHeatPumpTools$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.getHeatPumpTools),
            tap(() => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.UPDATING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.UPDATING_PROJECT'
                });
            }),
            withLatestFrom(this.settingsStore.languageCode$, this.globalStore.state$, (action, languageCode, globalState) => ({
                action,
                languageCode,
                globalState
            })),
            switchMap(({ globalState, action: { variantId }, languageCode }) => {
                const heatPumpToolsRequest = this.heatPumpIntermediaryMapper.toHeatPumpToolsRequest(globalState);
                return this.paramsControllerService.paramsControllerHeatPumpTools({ body: heatPumpToolsRequest }).pipe(
                    delay(0),
                    map(response => {
                        const projectRequest = { ...this.heatPumpIntermediaryMapper.heatPumpToolsToProjectRequest(response), languageCode };
                        return ProjectActions.updateProject({
                            id: globalState.project.selectedProject!.id!,
                            request: getUpdatedProperties(
                                globalState.project!.selectedProject!,
                                projectRequest as UpdateProjectRequestPartial,
                                ['household']
                            ),
                            successAction: ProjectActions.getHeatPumpToolsSuccess,
                            successActionOptions: { variantId }
                        });
                    }),
                    catchError(error => this.handleApiErrors(error.error as ErrorResponse))
                );
            })
        )
    );

    public saveHeatloadSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ProjectActions.saveHeatloadSuccess),
                tap(({ redirectUrl }) => {
                    this.router.navigate([redirectUrl]);
                })
            ),
        { dispatch: false }
    );

    public saveHeatload$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.saveHeatload),
            delay(0),
            tap(() => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.UPDATING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.UPDATING_PROJECT'
                });
            }),
            withLatestFrom(this.projectStore.project$, this.settingsStore.languageCode$, (action, project, languageCode) => ({
                action,
                project,
                request: { ...action.projectPatch, languageCode }
            })),
            map(({ action, project, request }) => ({
                redirectUrl: action.redirectUrl,
                id: project?.id!,
                body: getUpdatedProperties(project!, request as UpdateProjectRequestPartial, [
                    'jambWallHeight',
                    'slope1',
                    'slope2',
                    's1',
                    'height1',
                    'height2'
                ])
            })),
            switchMap(({ body, redirectUrl, id }) =>
                this.projectsControllerService.projectsControllerPatchProject({ id, body }).pipe(
                    delay(0),
                    map(() =>
                        ProjectActions.reloadProject({
                            id,
                            successActionOptions: { redirectUrl },
                            successAction: ProjectActions.saveHeatloadSuccess
                        })
                    ),
                    catchError(error => this.handleApiErrors(error.error as ErrorResponse))
                )
            )
        )
    );

    public savePhotovoltaicIntermediarySuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ProjectActions.savePhotovoltaicIntermediarySuccess),
                withLatestFrom(this.projectStore.project$, (action, project) => ({ ...action, project })),
                tap(({ project, variantId }) => {
                    this.externalToolUrlService.redirectToExternalToolByProjectId(ExternalTool.PhotovoltaicPlanner, {
                        projectId: project!.id,
                        variantId,
                        navigateTo: 'system',
                        countryCode: project!.countryCode
                    });
                })
            ),
        { dispatch: false }
    );

    public savePhotovoltaicIntermediary$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.savePhotovoltaicIntermediary),
            tap(() => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.UPDATING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.UPDATING_PROJECT'
                });
            }),
            withLatestFrom(this.settingsStore.languageCode$, this.projectStore.project$, (action, languageCode, project) => ({
                action,
                languageCode,
                project,
                request: { ...action.request, languageCode }
            })),
            map(({ action, project, request }) => ({
                variantId: action.variantId,
                projectId: project!.id!,
                body: getUpdatedProperties(project!, request as UpdateProjectRequestPartial)
            })),
            switchMap(({ variantId, projectId, body }) =>
                this.projectsControllerService.projectsControllerPatchProject({ id: projectId, body }).pipe(
                    map(() => ProjectActions.savePhotovoltaicIntermediarySuccess({ variantId })),
                    catchError(error => this.handleApiErrors(error.error as ErrorResponse))
                )
            )
        )
    );

    public createProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.createProject),
            tap(() => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.SAVING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.SAVING_PROJECT'
                });
            }),
            switchMap(({ request }) =>
                this.projectsControllerService.projectsControllerCreateProject({ body: request as CreateProjectRequestPost }).pipe(
                    delay(0),
                    map(project =>
                        ProjectActions.reloadProject({
                            id: project.id!,
                            successActionOptions: { redirectUrl: `${RouterLinks.Project}${project.id}` },
                            successAction: ProjectActions.getProjectSuccess
                        })
                    ),
                    catchError(error => this.handleApiErrors(error.error as ErrorResponse))
                )
            )
        )
    );

    public beforeUpdateProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.beforeUpdateProject),
            withLatestFrom(this.projectStore.project$, (action, project) => ({ action, project })),
            switchMap(({ action, project }) =>
                iif(
                    () =>
                        !LodashService.isNil(project?.building.heatLoadCalculations?.[0]) &&
                        this.hasProjectTypeChanged(project, action.request),
                    of(
                        // Remove heat load calculations and update project
                        ProjectActions.updateProject({
                            id: action.id,
                            request: {
                                ...action.request,
                                building: {
                                    ...action.request.building,
                                    heatLoadCalculations: []
                                }
                            },
                            successAction: ProjectActions.getProjectSuccess,
                            successActionOptions: { redirectUrl: action.redirectUrl }
                        })
                    ).pipe(
                        tap(() => {
                            const messageKey =
                                project?.building.heatLoadCalculations?.length! > 1
                                    ? 'SNACKBAR.HEATLOADS_REMOVED'
                                    : 'SNACKBAR.HEATLOAD_REMOVED';
                            const message = this.translate.instant(messageKey);
                            this.snackbarService.showInfo(message, { duration: 5000 });
                        })
                    ),
                    of(
                        ProjectActions.updateProject({
                            id: action.id,
                            request: action.request,
                            successAction: ProjectActions.getProjectSuccess,
                            successActionOptions: { redirectUrl: action.redirectUrl }
                        })
                    )
                )
            )
        )
    );

    public closeProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.closeProject),
            tap(() => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.CLOSING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.CLOSING_PROJECT'
                });
            }),
            switchMap(({ projectId }) =>
                this.projectsControllerService.projectsControllerFinalizeProject({ id: projectId }).pipe(
                    map(response =>
                        ProjectActions.closeProjectSuccess({
                            response
                        })
                    ),
                    catchError(error => this.handleApiErrors(error.error as ErrorResponse))
                )
            )
        )
    );

    public completeProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.completeProject),
            tap(() => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.COMPLETING_PROJECT',
                    loadingSubtext: 'LOADER.SUBTEXT.COMPLETING_PROJECT'
                });
            }),
            switchMap(({ projectId }) =>
                this.projectsControllerService.projectsControllerFinalizeProject({ id: projectId }).pipe(
                    map(response =>
                        ProjectActions.completeProjectSuccess({
                            response
                        })
                    ),
                    catchError(error => this.handleApiErrors(error.error as ErrorResponse))
                )
            )
        )
    );

    // CAVP-3070 Stop uploading Project PDF sections to V-API
    // public uploadProjectReportSection$ = createEffect(() =>
    //     this.actions$.pipe(
    //         ofType(ProjectActions.uploadProjectReportSection),
    //         mergeMap(payload =>
    //             this.reportsControllerService.reportsControllerUploadSection(payload).pipe(
    //                 map(() => ProjectActions.uploadProjectReportSectionSuccess()),
    //                 catchError(error => this.handleApiErrors(error.error as ErrorResponse))
    //             )
    //         )
    //     )
    // );

    public pdfReportGeneration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectActions.pdfReportGeneration),
            tap(() => {
                this.globalSpinnerService.next({
                    isShown: true,
                    loadingText: 'LOADER.TEXT.DOWNLOADING_REPORT',
                    loadingSubtext: 'LOADER.SUBTEXT.DOWNLOADING_REPORT',
                    hasCancel: true
                });
            }),
            mergeMap(payload =>
                // TODO: get rid of any type once the API is updated
                this.reportsControllerService.reportsControllerPdfReportGeneration(payload.params as any).pipe(
                    tap((blob: any) => {
                        this.downloadPdfReport(blob, payload.params.projectId, payload.params.reportType);
                    }),
                    map(() => ProjectActions.pdfReportGenerationSuccess()),
                    catchError(error => this.handleApiErrors(error.error as ErrorResponse))
                )
            )
        )
    );

    private downloadPdfReport(blob: Blob, projectId: string, reportType: string): void {
        const downloadUrl = this.windowService.nativeWindow.window.URL.createObjectURL(blob);
        const link = this.windowService.nativeWindow.document.createElement('a');
        link.setAttribute('href', downloadUrl);
        link.setAttribute('download', `${reportType}_${projectId}.pdf`);
        link.click();
        this.windowService.nativeWindow.window.URL.revokeObjectURL(downloadUrl);
    }

    private hasProjectTypeChanged(
        currentProject: ProjectResponseGetById | undefined,
        requestProject: UpdateProjectRequestPartial
    ): boolean {
        return !!requestProject.projectType && currentProject?.projectType !== requestProject.projectType;
    }

    private handleApiErrors(error: ErrorResponse): Observable<any> {
        this.globalSpinnerService.reset();
        const isOf409or422Status = [409, 422].includes(error.status);
        if (!isOf409or422Status) {
            return EMPTY;
        }
        this.errorService.showSnackbar(error.key as ApiErrors, { duration: 0 });
        const hasAddressCollission =
            error.key === ApiErrors.AddressUsedInOpenedProject || error.key === ApiErrors.AddressUsedInClosedProject;
        if (hasAddressCollission) {
            return of(ProjectActions.addressCollision());
        }
        return EMPTY;
    }

    private navigateTo404Page(error: ErrorResponse): void {
        if (error.status === 404) {
            this.router.navigate([RoutePaths.Error404]);
        }
    }
}
